import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control, Controller, Path, useForm, UseFormWatch,
} from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles, createStyles } from '@material-ui/styles';
import {
  Panel, PreventTransitionPrompt, Select, Switch, TextField, Tooltip, useDialogs,
} from '@michelin/acid-components';
import { useMutation, useQueryClient } from 'react-query';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate'; import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { CheckboxGroup } from 'components/CommercialServices/CheckboxGroup';
import { Checkbox, FormControlLabel, Grid } from '@material-ui/core';
import { saveCommerciaOverrides } from 'hooks/useCommercialOverrides';
import { useSnackbar } from 'notistack';
import { getOptionLabel, getOptions } from '@michelin/select-options-provider';
import { CommercialLocation } from 'components/Commercial/Fields';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { scrollToDisplayFirstError } from 'Utils';
import { commercialOverridesLabels, CommercialOverrides } from './CommercialOverridesFields';

interface CommercialOverridesPanelProps {
  overrides: CommercialOverrides
  action: 'edit' | 'view' | 'add',
  fromMassUpdate?: boolean,
  validations?: CommercialLocation['validations'],
}

const useStyles = makeStyles(() => createStyles({
  autoWith: {
    maxWidth: 60,
  },
}));

interface SideSwitchProps {
  control: Control<CommercialOverrides>,
  name: Path<CommercialOverrides>,
  readOnly: boolean,
  watch: UseFormWatch<CommercialOverrides>,
  checkbox: Path<CommercialOverrides>,
  onlyFalse?: boolean,
  onlyFalseReason?: string,
}

function SideSwtich({
  control, readOnly, name, watch, checkbox, onlyFalse, onlyFalseReason,
}: SideSwitchProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const value = watch(checkbox);
  if (!value) return null;
  const res = (
    <div style={{ display: 'inline-flex', position: 'relative', top: -4 }}>
      <Controller
        name={name}
        control={control}
        render={(item) => (
          <Switch
            id={item.field.name}
            readOnly={readOnly || onlyFalse}
            color="primary"
            hideLabel
            className={classes.autoWith}
            uncheckedLabel={t('No')}
            checkedLabel={t('Yes')}
            checked={!!item.field.value && (readOnly || !onlyFalse)}
            onChange={(event, newValue) => item.field.onChange(newValue && !onlyFalse)}
          />
        )}
      />
    </div>
  );

  if (onlyFalse && !readOnly) {
    return (
      <Tooltip title={onlyFalseReason || ''}>
        {res}
      </Tooltip>
    );
  }

  return res;
}

export function CommercialOverridesPanel({
  overrides, action, fromMassUpdate, validations,
}: CommercialOverridesPanelProps) {
  const { t } = useTranslation();
  const { stNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const {
    formState, getValues, reset, control, watch, setValue, handleSubmit,
  } = useForm({ defaultValues: overrides });
  const allowsEdit = permissions.allowsAction('admin_features.update_commercial_overrides');
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveCommerciaOverrides, {
    onSuccess: () => queryClient.invalidateQueries(['commercial-overrides', stNumber]),
  });
  const { errorDialog } = useDialogs();
  const readOnly: boolean = action === 'view';
  const isDirty = Object.keys(formState.dirtyFields).length > 0;
  const rankOptionList = getOptions('spp_service_rankings');
  const rankReasonList = getOptions('spp_service_rankings_reason');

  watch();

  useEffect(() => {
    if (action === 'view') reset();
  }, [action, reset]);

  useEffect(() => {
    reset(overrides);
  }, [overrides, reset]);

  const save = async (data: any) => {
    if (!allowsEdit) return;
    try {
      if (!isDirty) return;
      await saveMutation.mutateAsync({ stNumber, data });
      reset(getValues());
      history.push('view');
      enqueueSnackbar(t('Commercial Overrides information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial Products Data.'),
      );
    }
  };

  const onError = () => {
    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving certification information.'),
    );
    scrollToDisplayFirstError();
  };

  const onlyFalse = !fromMassUpdate && (!validations?.has_gps_location || !validations?.is_valid_service_address);
  const onlyFalseReason = t('You can\'t override this value to YES because location doesn\'t have GPS and a valid service address set.');

  return (
    <>
      {fromMassUpdate && allowsEdit && (
        <GenericActionButtonsMassUpdate
          isDirty={isDirty}
          getValues={getValues}
          reset={reset}
          handleSubmit={handleSubmit}
          validateAction="admin_features.update_commercial_overrides"
        />
      )}
      {!fromMassUpdate && (
        <GenericActionButtons
          isDirty={isDirty}
          isSaving={saveMutation.isLoading}
          onSave={handleSubmit(save, onError)}
          preventEdition={!allowsEdit}
          validateAction="admin_features.update_commercial_overrides"
          readOnly={readOnly}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CheckboxGroup
            getValues={getValues}
            title={t('Dealer Locator')}
            control={control}
            readOnly={readOnly}
            cols={12}
            labels={commercialOverridesLabels}
            setValue={setValue}
            watch={watch}
            items={[
              {
                key: 'overrides.on_tire_locator',
                onRender: () => (
                  <SideSwtich
                    watch={watch}
                    checkbox="overrides.on_tire_locator"
                    control={control}
                    name="overrides_value.on_tire_locator"
                    readOnly={readOnly}
                    onlyFalse={onlyFalse}
                    onlyFalseReason={onlyFalseReason}
                  />
                ),
                onChange: (data, newValue) => newValue && setValue('overrides_value.on_tire_locator', newValue && !getValues().overrides_original.on_tire_locator && !onlyFalse),
              },
              {
                key: 'overrides.on_mechanical_locator',
                onRender: () => (
                  <SideSwtich
                    watch={watch}
                    checkbox="overrides.on_mechanical_locator"
                    control={control}
                    name="overrides_value.on_mechanical_locator"
                    readOnly={readOnly}
                    onlyFalse={onlyFalse}
                    onlyFalseReason={onlyFalseReason}
                  />
                ),
                onChange: (data, newValue) => newValue && setValue('overrides_value.on_mechanical_locator', newValue && !getValues().overrides_original.on_mechanical_locator && !onlyFalse),
              },
            ]}
          />
          <Panel title={t('Rankings')} style={{ marginTop: 16 }}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={fromMassUpdate ? 12 : 6}>
                <FormControlLabel
                  control={(
                    <Controller
                      name="overrides.service_tier_ranking"
                      control={control}
                      render={(item) => (
                        <Checkbox
                          id="service_tier_ranking"
                          onChange={(e) => !readOnly && item.field.onChange(!!e.target.checked)}
                          color="primary"
                          readOnly={readOnly}
                          checked={item.field.value}
                        />
                      )}
                    />
                  )}
                  label={t('Service Rank')}
                />
              </Grid>
              {!fromMassUpdate && (
                <Grid item sm={12} md={6}>
                  <TextField
                    label={t('Current Service Rank')}
                    value={getOptionLabel('spp_service_rankings', getValues().overrides_original.service_tier_ranking || '')}
                    readOnly
                  />
                </Grid>
              )}
              {getValues().overrides.service_tier_ranking && (
                <>
                  <Grid item sm={12} md={6}>
                    <Controller
                      name="overrides_value.service_tier_ranking"
                      control={control}
                      shouldUnregister
                      rules={{ required: true }}
                      render={(item) => (
                        <Select
                          id="overrides_value.service_tier_ranking"
                          value={item.field.value || ''}
                          variant="standard"
                          fullWidth
                          readOnly={readOnly}
                          label={t('Rank')}
                          options={rankOptionList}
                          onChange={(e) => item.field.onChange(e.target.value)}
                          error={item.fieldState.invalid}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item sm={12} md={6}>
                    <Controller
                      name="overrides_value.service_tier_ranking_reason"
                      control={control}
                      shouldUnregister
                      rules={{ required: true }}
                      render={(item) => (
                        <Select
                          id="overrides_value.service_tier_ranking_reason"
                          value={item.field.value || ''}
                          variant="standard"
                          fullWidth
                          readOnly={readOnly}
                          label={t('Reason')}
                          options={rankReasonList}
                          onChange={(e) => item.field.onChange(e.target.value)}
                          error={item.fieldState.invalid}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <CheckboxGroup
            getValues={getValues}
            title={t('Networks & Memberships')}
            control={control}
            readOnly={readOnly}
            cols={12}
            labels={commercialOverridesLabels}
            setValue={setValue}
            watch={watch}
            items={[
              {
                key: 'overrides.mcsn_member',
                onRender: () => <SideSwtich watch={watch} checkbox="overrides.mcsn_member" control={control} name="overrides_value.mcsn_member" readOnly={readOnly} />,
                onChange: (data, newValue) => newValue && setValue('overrides_value.mcsn_member', newValue && !getValues().overrides_original.mcsn_member),
              },
            ]}
          />
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={isDirty}
      />
    </>
  );
}
